import type React from "react";
import { useMemo, useState } from "react";

import dayjs from "dayjs";

import { IconButton } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { DEFAULT_HOMEPAGES } from "app/components/modals/constants";
import { i18nFilter } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { BillingCycle } from "./BillingCycle/BillingCycle";
import { PackageDetails } from "./PackageDetails/PackageDetails";
import {
    BackIconButtonStyled,
    ContentWrapper,
    IconButtonContainer,
    IconButtonStyled,
    LeftContentContainer,
    RightContentContainer,
    Subtitle,
    Title,
    TitleWrapper,
    Wrapper,
} from "./StyledComponents";
import { Timeline } from "./Timeline/TimeLine";
import type { ICurrency, IPackagesPrice, PaymentPeriod } from "./types";
import { CIPackageId, SIPackageId } from "./types";
import {
    getAvailableCurrencies,
    getCurrency,
    getDefaultCurrency,
    getDefaultPaymentPeriod,
    getRelevantPackages,
} from "./utils";

const currency = "USD";

const availableCurrencies = ["USD"];

const translate = i18nFilter();

interface IAfterTrialPopup {
    packages: IPackagesPrice;
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    withCloseIcon: boolean;
    popUpHeight?: number;
    withBackButton?: boolean;
    onBackClick?: () => void;
    isSI: boolean;
    isSkipTrial: boolean;
}

export const AfterTrialPopup: React.FC<IAfterTrialPopup> = ({
    packages,
    setIsOpen,
    withCloseIcon,
    popUpHeight,
    withBackButton,
    onBackClick,
    isSI,
    isSkipTrial,
}) => {
    const defaultPeriod = getDefaultPaymentPeriod();

    const [selectedPeriod, setSelectedPeriod] = useState<PaymentPeriod>(defaultPeriod);
    const dueDate = dayjs().add(6, "days");

    const currencyCodes = getAvailableCurrencies(packages, availableCurrencies);

    const defaultCurrency = getDefaultCurrency(currencyCodes, currency);

    const currentCurrency = useMemo<ICurrency>(() => {
        return getCurrency(defaultCurrency);
    }, [defaultCurrency]);

    const { CompetitiveIntelligenceMonthly, CompetitiveIntelligenceAnnually } = getRelevantPackages(packages);

    const onNextClick = () => {
        const baseURL = `${swSettings.swsites.login}/journey/purchase-global`;
        const packageId = isSI ? SIPackageId : CIPackageId;
        const queryParams = new URLSearchParams({
            workspace: isSI ? DEFAULT_HOMEPAGES.SI : DEFAULT_HOMEPAGES.DMI,
            currency: currentCurrency.code,
            package: packageId,
            period: selectedPeriod,
            delayPayment: "true",
        });
        allTrackers.trackEvent("Package", "click", `choose package/${packageId}/${selectedPeriod}`);

        window.open(`${baseURL}?${queryParams}`);
    };
    const onChangePaymentPeriod = (period: PaymentPeriod) => {
        setSelectedPeriod(period);
    };

    return (
        <Wrapper popUpHeight={popUpHeight}>
            {withCloseIcon ? (
                <IconButtonStyled
                    iconName="clear"
                    iconSize="xs"
                    type="flat"
                    onClick={() => setIsOpen(false)}
                />
            ) : null}
            {withBackButton ? (
                <BackIconButtonStyled
                    iconName="arrow-left"
                    type="flat"
                    onClick={onBackClick}
                />
            ) : null}
            <ContentWrapper>
                <LeftContentContainer>
                    <TitleWrapper>
                        <Title>{translate(isSkipTrial ? "pro.packages.skip.title" : "pro.packages.title")}</Title>
                        <Subtitle>{translate("pro.packages.subtitle")}</Subtitle>
                    </TitleWrapper>
                    <Timeline
                        dueDate={dueDate}
                        currency={currentCurrency}
                    />
                </LeftContentContainer>
                <RightContentContainer>
                    <PackageDetails isSI={isSI} />
                    <BillingCycle
                        monthlyPackagePrice={CompetitiveIntelligenceMonthly}
                        yearlyPackagePrice={CompetitiveIntelligenceAnnually}
                        currency={currentCurrency}
                        onChange={onChangePaymentPeriod}
                        selectedPeriod={selectedPeriod}
                    />
                    <IconButtonContainer>
                        <IconButton
                            onClick={onNextClick}
                            iconName="arrow-right"
                            placement={"right"}
                        >
                            {translate("pro.packages.button")}
                        </IconButton>
                    </IconButtonContainer>
                </RightContentContainer>
            </ContentWrapper>
        </Wrapper>
    );
};
